import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from '../../utils/geo';

// OpenStreetMap (street map), Esri World Imagery (satellite) and the official 2025 orthophoto of
// Montenegro's Real Estate Administration (public WMS, CORS open), switchable in the corner.
const baseLayers = (t) => ({
    [t('map.layers.streets')]: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
    }),
    [t('map.layers.satellite')]: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
    }),
    [t('map.layers.orthophoto')]: L.tileLayer.wms('https://geoportal.uzn.me:442/erdas-iws/ogc/wms/OrtofotoCG', {
        layers: 'Ortofoto_DOF2025',
        format: 'image/jpeg',
        version: '1.3.0',
        tileSize: 512,
        maxZoom: 21,
        attribution: 'Ortofoto 2025 &copy; <a href="https://www.geoportal.co.me" target="_blank" rel="noopener">Uprava za nekretnine</a>',
    }),
});

/** A round coloured marker (no image assets needed, so nothing breaks in the bundle). */
export const pinIcon = (color = '#427898', size = 18, ring = '#fff') => L.divIcon({
    className: 'sd-pin',
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid ${ring};box-shadow:0 2px 6px rgba(15,23,42,.35)"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
});

/**
 * Thin Leaflet wrapper. `onReady(map)` runs once with the Leaflet map instance; callers add
 * their own markers. The layer choice is remembered per browser.
 */
export default function BaseMap({ height = 320, onReady, center, zoom, sx, scrollWheelZoom = true }) {
    const { t } = useTranslation();
    const ref = useRef(null);
    const mapRef = useRef(null);

    useEffect(() => {
        if (!ref.current || mapRef.current) return undefined;
        const map = L.map(ref.current, { zoomControl: true, scrollWheelZoom })
            .setView(center || DEFAULT_CENTER, zoom || DEFAULT_ZOOM);
        const layers = baseLayers(t);
        const names = Object.keys(layers);
        let saved = 0;
        try { saved = Number(localStorage.getItem('SD_MAP_LAYER')) || 0; } catch { /* ignore */ }
        layers[names[saved] || names[0]].addTo(map);
        L.control.layers(layers, null, { position: 'topright' }).addTo(map);
        map.on('baselayerchange', (e) => {
            try { localStorage.setItem('SD_MAP_LAYER', String(names.indexOf(e.name))); } catch { /* ignore */ }
        });
        mapRef.current = map;
        onReady?.(map);
        // Containers inside drawers/collapses get their size after mount.
        const id = setTimeout(() => map.invalidateSize(), 250);
        return () => { clearTimeout(id); map.remove(); mapRef.current = null; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box ref={ref} sx={{
            height, width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid #E2E8F0',
            '& .leaflet-container': { fontFamily: 'inherit' }, '& .sd-pin': { background: 'none', border: 0 },
            zIndex: 0, position: 'relative', ...sx,
        }} />
    );
}
